<template>
  <div class="page-main fin-page" v-loading="loading">
    <!-- 头部 -->
    <div class="fin-head">
      <div>
        <span class="fin-title">财务总览</span>
        <span class="fin-sub">
          应收 = 已审核核算单的「合同应收 - 已审核收款」；应付 = 采购明细金额 - 实付金额；毛利取核算单成本
        </span>
      </div>
      <div class="fin-actions">
        <el-button size="mini" type="primary" plain @click="load">刷新</el-button>
        <el-button size="mini" plain @click="go('/finance/receivable')">应收账龄</el-button>
        <el-button size="mini" plain @click="go('/finance/payable')">应付账龄</el-button>
        <el-button size="mini" plain @click="go('/finance/profit')">毛利分析</el-button>
      </div>
    </div>

    <!-- 主 KPI -->
    <div class="kpi-row">
      <div class="kpi-card">
        <div class="kpi-label">应收未收</div>
        <div class="kpi-value c-blue">{{ wan(ar.unpaid) }}</div>
        <div class="kpi-desc">
          合同应收 {{ wan(ar.total) }} · 已收 {{ wan(ar.received) }}
          <br />
          {{ ar.company_cnt || 0 }} 个客户 · {{ ar.bill_cnt || 0 }} 张未清单据
        </div>
        <div class="kpi-bar">
          <span class="bar-tip">逾期 90 天以上</span>
          <span class="c-red">{{ wan(ar.overdue_90) }}（{{ percent(ar.overdue_90, ar.unpaid) }}）</span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">本年回款（已审核）</div>
        <div class="kpi-value c-green">{{ wan(cash.year_received) }}</div>
        <div class="kpi-desc">
          本月回款 {{ wan(cash.month_received) }}
          <br />
          本年回款笔数 {{ cash.year_received_cnt || 0 }} 笔
        </div>
        <div class="kpi-bar">
          <span class="bar-tip">待审核收款</span>
          <span class="c-orange">{{ wan(cash.pending_amount) }}（{{ cash.pending_cnt || 0 }} 笔）</span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">应付未付</div>
        <div class="kpi-value c-orange">{{ wan(ap.unpaid) }}</div>
        <div class="kpi-desc">
          采购应付 {{ wan(ap.total) }} · 已付 {{ wan(ap.paid) }}
          <br />
          {{ ap.supplier_cnt || 0 }} 个供应商 · {{ ap.bill_cnt || 0 }} 张欠款单据
        </div>
        <div class="kpi-bar">
          <span class="bar-tip">逾期 90 天以上</span>
          <span class="c-red">{{ wan(ap.overdue_90) }}</span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">销售额 / 毛利（已审核核算单）</div>
        <div class="kpi-value c-blue">{{ wan(profit.profit) }}</div>
        <div class="kpi-desc">
          销售额 {{ wan(profit.sales) }} · 成本 {{ wan(profit.cost) }}
          <br />
          共 {{ profit.bill_cnt || 0 }} 张核算单
        </div>
        <div class="kpi-bar">
          <span class="bar-tip">综合毛利率</span>
          <span class="c-green">{{ profitRate }}%</span>
        </div>
      </div>
    </div>

    <!-- 财务提醒 -->
    <div class="fin-alert">
      <div class="alert-title">财务需要先处理的三件事</div>
      <div class="alert-line">
        ① 应收未收 {{ wan(ar.unpaid) }}，其中 {{ percent(ar.overdue_90, ar.unpaid) }} 超过 90 天；
        先核对「客户是否已付款但未登记收款」，再按客户催收。
      </div>
      <div class="alert-line">
        ② 待审核收款 {{ cash.pending_cnt || 0 }} 笔（{{ wan(cash.pending_amount) }}），
        审核后才计入「已收」，不审核会导致应收虚高。
      </div>
      <div class="alert-line">
        ③ 应付未付 {{ wan(ap.unpaid) }}，{{ ap.supplier_cnt || 0 }} 个供应商；付款登记后会从欠款里自动扣减。
      </div>
    </div>

    <!-- 趋势 -->
    <div class="fin-block">
      <div class="block-head">
        <span class="block-title">近 12 个月 销售 / 毛利 / 回款</span>
        <span class="block-tip">金额单位：万元；回款按收款单日期统计（已审核）</span>
      </div>
      <el-table :data="trend" size="mini" border height="300" :header-cell-style="headStyle">
        <el-table-column prop="ym" label="月份" width="100" align="center" />
        <el-table-column label="销售额" align="right">
          <template #default="{ row }">{{ wan(row.sales) }}</template>
        </el-table-column>
        <el-table-column label="成本" align="right">
          <template #default="{ row }">{{ wan(row.cost) }}</template>
        </el-table-column>
        <el-table-column label="毛利" align="right">
          <template #default="{ row }">
            <span :class="Number(row.profit) >= 0 ? 'c-green' : 'c-red'">{{ wan(row.profit) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="毛利率" align="right" width="90">
          <template #default="{ row }">{{ percent(row.profit, row.sales) }}%</template>
        </el-table-column>
        <el-table-column label="回款" align="right">
          <template #default="{ row }">{{ wan(row.received) }}</template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 欠款 Top -->
    <div class="fin-two-col">
      <div class="fin-block">
        <div class="block-head">
          <span class="block-title">欠款最多的 10 个客户</span>
          <span class="block-tip">点客户名进入应收账龄</span>
        </div>
        <el-table :data="topCustomers" size="mini" border height="320" :header-cell-style="headStyle">
          <el-table-column type="index" label="#" width="45" align="center" />
          <el-table-column prop="company_name" label="客户" show-overflow-tooltip>
            <template #default="{ row }">
              <el-link type="primary" @click="go('/finance/receivable')">{{ row.company_name }}</el-link>
            </template>
          </el-table-column>
          <el-table-column label="未收" width="110" align="right">
            <template #default="{ row }">{{ wan(row.unpaid) }}</template>
          </el-table-column>
          <el-table-column label="最长账龄" width="90" align="center">
            <template #default="{ row }">
              <span :class="ageClass(row.max_age)">{{ row.max_age }} 天</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="fin-block">
        <div class="block-head">
          <span class="block-title">欠款最多的 10 个供应商</span>
          <span class="block-tip">点供应商进入应付账龄</span>
        </div>
        <el-table :data="topSuppliers" size="mini" border height="320" :header-cell-style="headStyle">
          <el-table-column type="index" label="#" width="45" align="center" />
          <el-table-column prop="supplier_company_name" label="供应商" show-overflow-tooltip>
            <template #default="{ row }">
              <el-link type="primary" @click="go('/finance/payable')">
                {{ row.supplier_company_name }}
              </el-link>
            </template>
          </el-table-column>
          <el-table-column label="未付" width="110" align="right">
            <template #default="{ row }">{{ wan(row.unpaid) }}</template>
          </el-table-column>
          <el-table-column label="最长账龄" width="90" align="center">
            <template #default="{ row }">
              <span :class="ageClass(row.max_age)">{{ row.max_age }} 天</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 模块全景 -->
    <div class="fin-block">
      <div class="block-head">
        <span class="block-title">财务模块全景（与主流程对齐）</span>
        <span class="block-tip">已上线的可直接点进去用；待建设的按顺序补</span>
      </div>
      <el-table :data="modules" size="mini" border :header-cell-style="headStyle">
        <el-table-column prop="name" label="模块" width="140" />
        <el-table-column prop="flow" label="对应的主流程环节" width="240" />
        <el-table-column prop="data" label="数据来源 / 做法" />
        <el-table-column label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.status === '已上线'" size="mini" type="success">已上线</el-tag>
            <el-tag v-else-if="row.status === '旧版'" size="mini">旧版保留</el-tag>
            <el-tag v-else size="mini" type="info">待建设</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="入口" width="110" align="center">
          <template #default="{ row }">
            <el-link v-if="row.path" type="primary" @click="go(row.path)">打开</el-link>
            <span v-else class="c-gray">—</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      ar: {},
      ap: {},
      cash: {},
      profit: {},
      profitRate: 0,
      trend: [],
      topCustomers: [],
      topSuppliers: [],
      modules: [
        {
          name: '财务总览',
          flow: '全流程（老板/财务看数）',
          data: '汇总应收、应付、回款、毛利',
          status: '已上线',
          path: '/finance/overview'
        },
        {
          name: '应收账龄',
          flow: '09 收款 / 应收（核算单 → 收款单）',
          data: '核算单 + 收款单',
          status: '已上线',
          path: '/finance/receivable'
        },
        {
          name: '应付账龄',
          flow: '09 收款 / 应付（采购单 → 付款）',
          data: '采购单 + 采购付款',
          status: '已上线',
          path: '/finance/payable'
        },
        {
          name: '毛利分析',
          flow: '01 核算单（成本、报价）',
          data: '核算单成本价 / 利润',
          status: '已上线',
          path: '/finance/profit'
        },
        {
          name: '应收列表',
          flow: '09 收款 / 应收',
          data: '按单据逐张开收款单、审核',
          status: '旧版',
          path: '/finance/payin/list'
        },
        {
          name: '应付列表',
          flow: '09 收款 / 应付',
          data: '按采购单逐张登记付款',
          status: '旧版',
          path: '/finance/payout/list'
        },
        {
          name: '开票管理',
          flow: '09 收款 / 应收',
          data: '销项/进项发票登记，与收款、核算单勾稽',
          status: '待建设',
          path: ''
        },
        {
          name: '费用管理',
          flow: '制造线 / 商务线（运费、加工费、杂费）',
          data: '其他费用表，摊到核算单算真实毛利',
          status: '待建设',
          path: ''
        },
        {
          name: '资金流水',
          flow: '09 收款 / 应付',
          data: '按银行账户看收付款流水与余额（需补期初余额）',
          status: '待建设',
          path: ''
        }
      ]
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      this.$api({ method: 'post', url: '/admin/gcvip/finance/overview', data: {} })
        .then((res) => {
          if (res.data.state === 'ok') {
            this.ar = res.data.ar || {}
            this.ap = res.data.ap || {}
            this.cash = res.data.cash || {}
            this.profit = res.data.profit || {}
            this.profitRate = Number(res.data.profitRate || 0).toFixed(2)
            this.trend = res.data.trend || []
            this.topCustomers = res.data.topCustomers || []
            this.topSuppliers = res.data.topSuppliers || []
          } else {
            this.$message.error(res.data.msg || '查询失败')
          }
        })
        .catch(() => {})
        .then(() => {
          this.loading = false
        })
    },
    go(path) {
      if (!path) return
      this.$router.push(path)
    },
    headStyle() {
      return { background: '#f5f7fa', color: '#606266', padding: '6px 0' }
    },
    wan(value) {
      let num = Number(value || 0)
      if (isNaN(num)) num = 0
      return (num / 10000).toFixed(2) + ' 万'
    },
    percent(part, total) {
      let p = Number(part || 0)
      let t = Number(total || 0)
      if (!t) return '0.00'
      return ((p * 100) / t).toFixed(2)
    },
    ageClass(age) {
      let n = Number(age || 0)
      if (n > 90) return 'c-red'
      if (n > 60) return 'c-orange'
      return 'c-gray'
    }
  }
}
</script>

<style lang="less" scoped>
.fin-page {
  padding: 10px 12px 20px;
  overflow-y: auto;
  height: calc(100vh - 86px);
  box-sizing: border-box;
}
.fin-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  .fin-title {
    font-size: 17px;
    font-weight: 600;
    margin-right: 10px;
  }
  .fin-sub {
    font-size: 12px;
    color: #909399;
  }
  .fin-actions {
    white-space: nowrap;
    .el-button + .el-button {
      margin-left: 6px;
    }
  }
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}
.kpi-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px 14px;
  .kpi-label {
    font-size: 13px;
    color: #606266;
  }
  .kpi-value {
    font-size: 24px;
    font-weight: 600;
    margin: 6px 0;
  }
  .kpi-desc {
    font-size: 12px;
    color: #909399;
    line-height: 18px;
    min-height: 36px;
  }
  .kpi-bar {
    display: flex;
    justify-content: space-between;
    border-top: 1px dashed #ebeef5;
    padding-top: 6px;
    font-size: 12px;
    .bar-tip {
      color: #909399;
    }
  }
}
.fin-alert {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 4px;
  padding: 10px 14px;
  margin-bottom: 10px;
  .alert-title {
    font-size: 13px;
    font-weight: 600;
    color: #8a5a20;
    margin-bottom: 4px;
  }
  .alert-line {
    font-size: 12px;
    color: #8a5a20;
    line-height: 22px;
  }
}
.fin-block {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px 12px 12px;
  margin-bottom: 10px;
  .block-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 8px;
    .block-title {
      font-size: 14px;
      font-weight: 600;
      border-left: 3px solid #396bf6;
      padding-left: 8px;
    }
    .block-tip {
      font-size: 12px;
      color: #a8abb2;
    }
  }
}
.fin-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.c-blue {
  color: #396bf6;
}
.c-green {
  color: #46bd87;
}
.c-orange {
  color: #e6a23c;
}
.c-red {
  color: #f56c6c;
}
.c-gray {
  color: #909399;
}
@media screen and (max-width: 1400px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media screen and (max-width: 1100px) {
  .fin-two-col {
    grid-template-columns: 1fr;
  }
}
</style>
